// src/components/Dashboard/ClaimSchedule.jsx
// Módulo 6 del spec del cliente: agenda de inspecciones/citas por reclamo.
// Solo el admin crea/edita/borra eventos (ver claimEventService.js); el
// ajustador solo los ve — coherente con Notes Log.
import React, { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import ConfirmModal from '../common/ConfirmModal';
import { LanguageContext } from '../../App';
import { useAuth } from '../../contexts/AuthContext';
import {
  getClaimEvents,
  createClaimEvent,
  updateClaimEvent,
  deleteClaimEvent,
  retryClaimEventSync,
  backfillWorkspaceCalendar,
  EVENT_TYPES,
  EVENT_TYPE_LABELS
} from '../../services/claimEventService';
import { DEFAULT_EVENT_TIMEZONE, EVENT_TIMEZONES, eventTimezone, eventTimezoneLabel, isEventPast, timezoneForState } from '../../services/eventTimezones';

const emptyForm = { type: 'initial_inspection', date: '', time: '', timezone: DEFAULT_EVENT_TIMEZONE, durationMinutes: 60, location: '', confirmed: false, notes: '' };

const formatEventDate = (date, language) => {
  if (!date) return '—';
  const parsed = new Date(`${date}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat(language === 'es' ? 'es-ES' : 'en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
  }).format(parsed);
};

const ClaimSchedule = ({ claimRecordId, claimRecord }) => {
  const { language } = useContext(LanguageContext);
  const { isAdmin, isAuthenticated } = useAuth();
  // Crear, confirmar, editar y cancelar citas: cualquier usuario con acceso al reclamo (la sincronización masiva
  // de calendarios y reprocesar errores siguen siendo del Super Admin).
  const canManageEvents = isAuthenticated;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillProgress, setBackfillProgress] = useState(null);

  const load = async () => {
    if (!claimRecordId) return;
    setLoading(true);
    try {
      setEvents(await getClaimEvents(claimRecordId));
    } catch (err) {
      toast.error(language === 'es' ? 'No se pudo cargar la agenda.' : 'Could not load the schedule.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimRecordId]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleOpenNew = () => {
    const address = [claimRecord?.lossAddressStreet, claimRecord?.lossAddressCity, claimRecord?.lossAddressState, claimRecord?.lossAddressZip].filter(Boolean).join(', ') || claimRecord?.clientAddress || '';
    setForm({ ...emptyForm, timezone: timezoneForState(claimRecord?.lossAddressState), location: address });
    setEditingId(null);
    setShowForm(true);
  };

  const handleOpenEdit = (event) => {
    setForm({ type: event.type, date: event.date, time: event.time || '', timezone: eventTimezone(event), durationMinutes: event.durationMinutes || 60, location: event.location || '', confirmed: !!event.confirmed, notes: event.notes || '' });
    setEditingId(event.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.date) {
      toast.error(language === 'es' ? 'La fecha es obligatoria.' : 'Date is required.');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const result = await updateClaimEvent(editingId, form);
        toast[result.syncError || result.invitationError ? 'error' : 'success'](result.syncError || result.invitationError || (language === 'es' ? 'Evento actualizado.' : 'Event updated.'));
      } else {
        const result = await createClaimEvent(claimRecordId, form);
        toast[result.syncError || result.invitationError ? 'error' : 'success'](result.syncError || result.invitationError || (language === 'es' ? 'Evento agendado.' : 'Event scheduled.'));
      }
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(err.message || (language === 'es' ? 'No se pudo guardar el evento.' : 'Could not save the event.'));
    } finally {
      setSaving(false);
    }
  };

  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const result = await backfillWorkspaceCalendar(setBackfillProgress);
      toast[result.failed ? 'error' : 'success'](language === 'es'
        ? `${result.synced} citas copiadas a Workspace; ${result.failed} pendientes.`
        : `${result.synced} appointments copied to Workspace; ${result.failed} pending.`);
      await load();
    } catch (error) { toast.error(error.message); }
    finally { setBackfilling(false); }
  };

  const handleRetrySync = async (event) => {
    try {
      const result = await retryClaimEventSync(event);
      if (result.syncError || result.invitationError) toast.error(result.syncError || result.invitationError);
      else toast.success(language === 'es' ? 'Calendarios sincronizados.' : 'Calendars synced.');
      await load();
    } catch (error) { toast.error(error.message); }
  };

  // Confirmación con el diálogo de la app (antes window.confirm).
  const [pendingDelete, setPendingDelete] = useState(null);
  const handleDelete = event => setPendingDelete(event);
  const confirmDelete = async () => {
    const event = pendingDelete;
    setPendingDelete(null);
    try {
      await deleteClaimEvent(event.id);
      setEvents(current => current.filter(ev => ev.id !== event.id));
      toast.success(language === 'es' ? 'Evento eliminado.' : 'Event deleted.');
    } catch (err) {
      toast.error(language === 'es' ? 'No se pudo eliminar el evento.' : 'Could not delete the event.');
    }
  };

  const toggleConfirmed = async (event) => {
    try {
      await updateClaimEvent(event.id, { ...event, confirmed: !event.confirmed });
      setEvents(current => current.map(ev => ev.id === event.id ? { ...ev, confirmed: !ev.confirmed } : ev));
    } catch (err) {
      toast.error(language === 'es' ? 'No se pudo actualizar.' : 'Could not update.');
    }
  };

  if (!claimRecordId) return null;

  return (
    <div className="cd-attach-section">
      <div className="cd-detail-checks-header">
        <div>
          <h2 className="cd-detail-checks-title">{language === 'es' ? 'Citas e inspecciones' : 'Appointments and inspections'}</h2>
          <p className="cd-schedule-description">{language === 'es' ? 'Próximas citas primero; las actividades pasadas quedan al final.' : 'Upcoming appointments first; past activities follow.'}</p>
        </div>
        {isAdmin && <button type="button" className="cd-attach-mini-btn" onClick={handleBackfill} disabled={backfilling} title={language === 'es' ? 'Copiar citas anteriores de todos los reclamos a Workspace, sin enviar invitaciones' : 'Copy earlier appointments from all claims to Workspace without invitations'}>{backfilling ? `${backfillProgress?.synced || 0}/${backfillProgress?.total || 0}` : (language === 'es' ? 'Respaldar citas anteriores' : 'Back up earlier appointments')}</button>}
        {canManageEvents && (
          <button type="button" className="cd-attach-toolbar-btn cd-attach-toolbar-btn-wide" onClick={handleOpenNew} title={language === 'es' ? 'Agendar evento' : 'Schedule event'}>
            <i className="fa-solid fa-calendar-plus"></i> <span>{language === 'es' ? 'Agregar cita' : 'Add appointment'}</span>
          </button>
        )}
      </div>

      <div className="cd-attach-explorer">
        {showForm && (
          <form className="cd-event-form" onSubmit={handleSubmit}>
            <div className="cd-event-form-grid">
              <div className="cc-input-group">
                <label className="cc-label">{language === 'es' ? 'Tipo de evento' : 'Event type'}</label>
                <select className="cc-select" value={form.type} onChange={(e) => handleChange('type', e.target.value)}>
                  {EVENT_TYPES.map(t => <option key={t} value={t}>{EVENT_TYPE_LABELS[t][language === 'es' ? 'es' : 'en']}</option>)}
                </select>
              </div>
              <div className="cc-input-group">
                <label className="cc-label">{language === 'es' ? 'Fecha' : 'Date'} *</label>
                <input type="date" className="cc-input" value={form.date} onChange={(e) => handleChange('date', e.target.value)} required />
              </div>
              <div className="cc-input-group">
                <label className="cc-label">{language === 'es' ? 'Hora local de inicio' : 'Local start time'}</label>
                <input type="time" className="cc-input" value={form.time} onChange={(e) => handleChange('time', e.target.value)} />
              </div>
              <div className="cc-input-group">
                <label className="cc-label">{language === 'es' ? 'Zona horaria de la cita' : 'Appointment time zone'}</label>
                <select className="cc-select" value={form.timezone} onChange={(e) => handleChange('timezone', e.target.value)}>
                  {EVENT_TIMEZONES.map(zone => <option key={zone.value} value={zone.value}>{zone[language === 'es' ? 'es' : 'en']}</option>)}
                </select>
              </div>
              <div className="cc-input-group">
                <label className="cc-label">{language === 'es' ? 'Duración (minutos)' : 'Duration (minutes)'}</label>
                <input type="number" className="cc-input" min="15" max="1440" step="15" value={form.durationMinutes} onChange={(e) => handleChange('durationMinutes', e.target.value)} />
              </div>
            </div>
            <p className="cd-schedule-description">{language === 'es' ? 'La hora se interpreta en la zona elegida. Verifica la zona en estados con varias zonas, como Florida, Texas o Tennessee.' : 'The time uses the selected zone. Check it in states with multiple zones, such as Florida, Texas, or Tennessee.'}</p>
            <div className="cc-input-group">
              <label className="cc-label">{language === 'es' ? 'Dirección de la cita' : 'Appointment address'}</label>
              <input type="text" className="cc-input" value={form.location} onChange={(e) => handleChange('location', e.target.value)} />
            </div>
            <p className="cd-schedule-description">{language === 'es' ? 'Todas las citas se guardan en Google Workspace de CSR. Con hora, Adjustify también envía su invitación por Zoho; las demás empresas reciben una invitación por correo compatible con su calendario.' : 'All appointments are saved in CSR Google Workspace. Timed Adjustify appointments also invite through Zoho; other companies receive a calendar email invitation.'}</p>
            <div className="cc-input-group">
              <label className="cc-label">{language === 'es' ? 'Notas del evento' : 'Event notes'}</label>
              <textarea className="cd-notes-textarea" rows={2} value={form.notes} onChange={(e) => handleChange('notes', e.target.value)} />
            </div>
            <label className="cc-checkbox-row">
              <input type="checkbox" checked={form.confirmed} onChange={(e) => handleChange('confirmed', e.target.checked)} />
              {language === 'es' ? 'Cita confirmada' : 'Appointment confirmed'}
            </label>
            <div className="cd-event-form-actions">
              <button type="submit" className="cd-btn cd-btn-check" disabled={saving}>
                <i className="fa-solid fa-check"></i> {editingId ? (language === 'es' ? 'Guardar cambios' : 'Save changes') : (language === 'es' ? 'Agendar' : 'Schedule')}
              </button>
              <button type="button" className="cd-attach-mini-btn" onClick={() => setShowForm(false)} disabled={saving}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
          </form>
        )}

        {loading && <p className="cd-attach-empty">{language === 'es' ? 'Cargando agenda...' : 'Loading schedule...'}</p>}
        {!loading && events.length === 0 && !showForm && (
          <p className="cd-attach-empty">{language === 'es' ? 'Todavía no hay eventos agendados.' : 'No events scheduled yet.'}</p>
        )}
        {!loading && events.length > 0 && (() => {
          const upcoming = events.filter(e => !isEventPast(e)).sort((a, b) => `${a.date || ''}T${a.time || ''}`.localeCompare(`${b.date || ''}T${b.time || ''}`));
          const history = events.filter(isEventPast).sort((a, b) => `${b.date || ''}T${b.time || ''}`.localeCompare(`${a.date || ''}T${a.time || ''}`));
          const renderEvent = (event) => (
            <div key={event.id} className={`cd-event-card ${isEventPast(event) ? 'cd-event-past' : ''}`}>
              <div className="cd-event-card-main">
                {/* La fecha va primero y más grande — es el dato que
                    responde "cuándo", el tipo de evento es secundario. */}
                <span className="cd-event-datetime cd-event-date-primary">
                  <i className="fa-regular fa-calendar"></i> {formatEventDate(event.date, language)}
                  {event.time && <span> · {event.time} · {eventTimezoneLabel(eventTimezone(event), language)}</span>}
                </span>
                <span className="cd-event-type cd-event-type-secondary">{EVENT_TYPE_LABELS[event.type]?.[language === 'es' ? 'es' : 'en'] || event.type}</span>
                <button
                  type="button"
                  className={`cd-event-confirm-badge ${event.confirmed ? 'cd-event-confirm-yes' : 'cd-event-confirm-no'}`}
                  onClick={() => canManageEvents && toggleConfirmed(event)}
                  disabled={!canManageEvents}
                >
                  <i className={`fa-solid ${event.confirmed ? 'fa-check' : 'fa-clock'}`}></i>
                  {event.confirmed ? (language === 'es' ? 'Confirmada' : 'Confirmed') : (language === 'es' ? 'Pendiente' : 'Pending')}
                </button>
              </div>
              {event.location && <p className="cd-schedule-description"><i className="fa-solid fa-location-dot"></i> {event.location}</p>}
              {event.notes && <p className="cd-note-text">{event.notes}</p>}
              {event.googleStatus === 'synced' && <p className="cd-schedule-description">{language === 'es' ? 'Copia en CSR Workspace' : 'Copy in CSR Workspace'} {event.googleUrl && <a href={event.googleUrl} target="_blank" rel="noopener noreferrer">{language === 'es' ? 'Abrir' : 'Open'}</a>}</p>}
              {event.zohoStatus === 'synced' && <p className="cd-schedule-description">{language === 'es' ? 'Invitación enviada por Zoho' : 'Invitation sent through Zoho'} {event.zohoUrl && <a href={event.zohoUrl} target="_blank" rel="noopener noreferrer">{language === 'es' ? 'Abrir' : 'Open'}</a>}</p>}
              {(event.syncError || event.invitationError || event.googleStatus === 'failed' || event.zohoStatus === 'failed') && <div className="cd-event-sync-error"><span>{isAdmin ? (event.syncError || event.invitationError || (language === 'es' ? 'Sincronización pendiente' : 'Sync pending')) : (language === 'es' ? 'Sincronización pendiente' : 'Sync pending')}</span>{isAdmin && <button type="button" className="cd-attach-mini-btn" onClick={() => handleRetrySync(event)}>{language === 'es' ? 'Reintentar' : 'Retry'}</button>}</div>}
              {canManageEvents && (
                <div className="cd-note-actions">
                  <button type="button" className="cd-attach-mini-btn" onClick={() => handleOpenEdit(event)} title={language === 'es' ? 'Editar' : 'Edit'}>
                    <i className="fa-solid fa-pen"></i>
                  </button>
                  <button type="button" className="cd-attach-mini-btn cd-attach-mini-danger" onClick={() => handleDelete(event)} title={language === 'es' ? 'Eliminar' : 'Delete'}>
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </div>
              )}
            </div>
          );
          return (
            <>
              {upcoming.length > 0 && (
                <>
                  <h3 className="cd-schedule-group-title">{language === 'es' ? 'Próximas citas' : 'Upcoming'}</h3>
                  <div className="cd-notes-list">{upcoming.map(renderEvent)}</div>
                </>
              )}
              {history.length > 0 && (
                <>
                  <h3 className="cd-schedule-group-title">{language === 'es' ? 'Historial' : 'History'}</h3>
                  <div className="cd-notes-list">{history.map(renderEvent)}</div>
                </>
              )}
            </>
          );
        })()}
      </div>
      <ConfirmModal
        open={!!pendingDelete}
        icon="fa-trash"
        danger
        title={language === 'es' ? '¿Eliminar este evento?' : 'Delete this event?'}
        message={language === 'es' ? 'No se puede deshacer.' : 'This cannot be undone.'}
        confirmLabel={language === 'es' ? 'Eliminar' : 'Delete'}
        cancelLabel={language === 'es' ? 'Cancelar' : 'Cancel'}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default ClaimSchedule;
